/**
 * Session appointment fields shared by the provider calendar modal and the
 * in-session scheduler, so both surfaces capture identical information.
 *
 * `SessionIdentityFields` is the session-only block (client, provider, session
 * type, attendees); the composed `SessionAppointmentFormFields` adds the generic
 * date/time, recurrence, and location blocks for callers that want the whole form.
 */

import { MultiSelect, Select } from '@mantine/core';
import { IconClipboardList, IconUser, IconUsers } from '@tabler/icons-react';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  AppointmentDateTimeFields,
  AppointmentLocationFields,
  AppointmentRecurrenceFields,
  type AppointmentLocationSite,
  type AppointmentModality,
  type OverlayPortalProps,
} from './AppointmentSharedFields';
import { parseTimeLabel12h } from './providerAvailabilityValidation';
import { endTimeForDuration } from './sessionSchedulerOptions';
import { applyZonedWallClock } from '@/lib/unifyDateFormat';
import { DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE } from '@unify/fhir-model/resources/schedule';
import type { LiveCalendarEpisodeDetail } from './useCalendarAppointmentFormOptions';
import type { MinuteInterval } from '@/pages/calendar/components/roomSchedulingDemoData';
import type { Equipment } from '@/pages/settings/components/EquipmentManagementSection';
import type { Room } from '@/pages/settings/components/RoomManagementSection';

/** `AppointmentFormValues` subset the session fields read and write. */
export interface SessionAppointmentFieldsValue {
  episodeOfCareId: string | null;
  client: string | null;
  provider: string;
  sessionType: string;
  sessionAttendees: string[];
  attendanceStatus: string;
  date: Date | null;
  startTime: string;
  endTime: string;
  recurringEnabled: boolean;
  recurringInterval: number;
  recurringTimeframe: string;
  recurringDaysOfWeek: string[];
  recurringEndType: 'occurrences' | 'date';
  recurringEndDate: Date | null;
  recurringOccurrences: number;
  modality: AppointmentModality;
  siteId: string;
  telehealthFromOffice: boolean;
  officeForTelehealth: string;
  roomId: string | null;
  deviceIds: string[];
}

export type SessionAppointmentFieldErrors = Partial<
  Record<keyof SessionAppointmentFieldsValue, ReactNode>
>;

export interface SessionIdentityFieldsProps extends OverlayPortalProps {
  values: Pick<
    SessionAppointmentFieldsValue,
    'episodeOfCareId' | 'provider' | 'sessionType' | 'sessionAttendees' | 'attendanceStatus'
  >;
  onChange: (patch: Partial<SessionAppointmentFieldsValue>) => void;
  errors?: SessionAppointmentFieldErrors;
  episodeOptions: { value: string; label: string }[];
  episodesLoading?: boolean;
  /** Drives the cascade when the client changes (provider, attendees, session type reset). */
  episodeDetails: LiveCalendarEpisodeDetail[];
  providerOptions: { value: string; label: string }[];
  providersLoading?: boolean;
  sessionTypeOptions: { value: string; label: string }[];
  attendeeOptions: { value: string; label: string }[];
  /** Unused leftover; appointment-level status is derived when attendance is saved; a signed note locks the write. */
  statusOptions?: { value: string; label: string; disabled?: boolean }[];
  /** Editing an existing appointment: shows attendance status and keeps fields enabled. */
  editing?: boolean;
  /** Client/episode is fixed by the surrounding context (in-session scheduler). */
  lockEpisode?: boolean;
  /**
   * Hide the client and attendee pickers. A group session's roster comes from the
   * cohort, so there is no single client to choose here.
   */
  hideClientSelection?: boolean;
  /** Group co-facilitator picker, rendered under Provider when supplied. */
  coFacilitators?: {
    options: { value: string; label: string }[];
    value: string[];
    onChange: (ids: string[]) => void;
  };
  /** Hide the Attendees multi-select (appointment-page edit modal). */
  hideAttendees?: boolean;
  onAttendanceStatusChange?: (value: string) => void;
  /** Session type id → minutes; picking a type advances the end time to match. */
  sessionTypeDurations?: ReadonlyMap<string, number>;
  /** Current start time label, needed to recompute the end time. */
  startTime?: string;
}

export function SessionIdentityFields({
  values,
  onChange,
  errors,
  episodeOptions,
  episodesLoading,
  episodeDetails,
  providerOptions,
  providersLoading,
  sessionTypeOptions,
  attendeeOptions,
  statusOptions: _statusOptions,
  editing = false,
  lockEpisode = false,
  hideClientSelection = false,
  hideAttendees = false,
  coFacilitators,
  onAttendanceStatusChange: _onAttendanceStatusChange,
  sessionTypeDurations,
  startTime,
  comboboxProps,
}: SessionIdentityFieldsProps): JSX.Element {
  const clientPickerLocked = lockEpisode || Boolean(episodesLoading);
  const needsProvider = !values.provider && !editing && !hideClientSelection;
  const needsEpisode = !values.episodeOfCareId && !editing && !hideClientSelection;

  const handleSessionTypeChange = (sessionType: string): void => {
    const duration = sessionTypeDurations?.get(sessionType);
    const endTime =
      duration && startTime ? endTimeForDuration(startTime, duration) : undefined;
    onChange({ sessionType, ...(endTime ? { endTime } : {}) });
  };

  const handleProviderChange = (providerId: string | null): void => {
    if (editing || lockEpisode) {
      onChange({ provider: providerId ?? '' });
      return;
    }
    onChange({
      provider: providerId ?? '',
      episodeOfCareId: null,
      client: null,
      sessionAttendees: [],
      sessionType: '',
    });
  };

  const handleEpisodeChange = (episodeId: string | null): void => {
    if (!episodeId) {
      onChange({
        episodeOfCareId: null,
        client: null,
        sessionAttendees: [],
        sessionType: '',
      });
      return;
    }
    const episode = episodeDetails.find((e) => e.id === episodeId);
    if (!episode) {
      onChange({ episodeOfCareId: episodeId });
      return;
    }
    onChange({
      episodeOfCareId: episodeId,
      client: episode.clientName,
      provider: values.provider || episode.careManagerId || '',
      /** Cleared so the episode type's standard session type re-defaults. */
      sessionType: '',
      sessionAttendees: [
        `Patient/${episode.patientId}`,
        ...episode.participantRefs.map((p) => `RelatedPerson/${p.id}`),
      ],
    });
  };

  return (
    <>
      <Select
        label="Provider"
        placeholder={
          providersLoading
            ? 'Loading providers…'
            : providerOptions.length === 0
              ? 'No providers available'
              : 'Select provider'
        }
        value={values.provider || null}
        onChange={handleProviderChange}
        withAsterisk
        leftSection={<IconUser size={16} />}
        data={providerOptions}
        disabled={providersLoading || providerOptions.length === 0}
        searchable
        error={errors?.provider}
        comboboxProps={comboboxProps}
      />
      {coFacilitators && (
        <MultiSelect
          label="Co-facilitator(s)"
          placeholder="Select co-facilitators"
          value={coFacilitators.value}
          onChange={coFacilitators.onChange}
          leftSection={<IconUsers size={16} />}
          data={coFacilitators.options}
          searchable
          clearable
          comboboxProps={comboboxProps}
        />
      )}
      {!hideClientSelection && (
        <Select
          label="Client"
          placeholder={
            needsProvider
              ? 'Select a provider first'
              : episodesLoading
                ? 'Loading clients…'
                : episodeOptions.length === 0
                  ? 'No clients available'
                  : 'Select client'
          }
          value={values.episodeOfCareId}
          onChange={handleEpisodeChange}
          data={episodeOptions}
          withAsterisk
          leftSection={<IconClipboardList size={16} />}
          clearable={!lockEpisode}
          disabled={clientPickerLocked || needsProvider || episodeOptions.length === 0}
          nothingFoundMessage="No matching clients"
          searchable={!lockEpisode}
          error={errors?.episodeOfCareId}
          comboboxProps={comboboxProps}
        />
      )}
      <Select
        label="Session type"
        placeholder={needsEpisode ? 'Select a client first' : undefined}
        value={values.sessionType || null}
        onChange={(v) => handleSessionTypeChange(v ?? '')}
        withAsterisk
        searchable
        data={sessionTypeOptions}
        nothingFoundMessage="No session types enabled for this provider — ask a practice admin"
        disabled={needsEpisode}
        error={errors?.sessionType}
        comboboxProps={comboboxProps}
      />
      {!hideClientSelection && !hideAttendees && (
        <MultiSelect
          label="Attendees"
          placeholder={
            needsEpisode
              ? 'Select a client first'
              : values.sessionAttendees.length === 0
                ? 'Select attendees'
                : undefined
          }
          value={values.sessionAttendees}
          onChange={(v) => onChange({ sessionAttendees: v })}
          leftSection={<IconUsers size={16} />}
          data={attendeeOptions}
          disabled={needsEpisode}
          clearable
          comboboxProps={comboboxProps}
        />
      )}
    </>
  );
}

export interface SessionAppointmentFormFieldsProps
  extends Omit<SessionIdentityFieldsProps, 'values'>,
    OverlayPortalProps {
  values: SessionAppointmentFieldsValue;
  sites: AppointmentLocationSite[];
  siteOptions: { value: string; label: string }[];
  firstSiteId: string;
  rooms: Room[];
  portableDevices: Equipment[];
  fixedInRoom: Equipment[];
  busyByRoom?: Map<string, MinuteInterval[]>;
  busyByDevice?: Map<string, MinuteInterval[]>;
  /** Existing recurring series being edited — forces the recurrence panel open. */
  recurringSeries?: boolean;
  onViewRoomAvailability?: () => void;
  displayTimeZone?: string;
}

/** Whole session form: identity fields plus the generic schedule and location blocks. */
export function SessionAppointmentFormFields({
  values,
  onChange,
  errors,
  sites,
  siteOptions,
  firstSiteId,
  rooms,
  portableDevices,
  fixedInRoom,
  busyByRoom,
  busyByDevice,
  recurringSeries = false,
  onViewRoomAvailability,
  displayTimeZone = DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE,
  comboboxProps,
  popoverProps,
  ...identityProps
}: SessionAppointmentFormFieldsProps): JSX.Element {
  const { bookingStart, bookingEnd } = useMemo(() => {
    const s = parseTimeLabel12h(values.startTime);
    const e = parseTimeLabel12h(values.endTime);
    if (!values.date || !s || !e) return { bookingStart: null, bookingEnd: null };
    const start = applyZonedWallClock(values.date, s.hour, s.minute, displayTimeZone);
    const end = applyZonedWallClock(values.date, e.hour, e.minute, displayTimeZone);
    if (end <= start) return { bookingStart: null, bookingEnd: null };
    return { bookingStart: start, bookingEnd: end };
  }, [values.date, values.startTime, values.endTime, displayTimeZone]);
  return (
    <>
      <SessionIdentityFields
        {...identityProps}
        values={values}
        onChange={onChange}
        errors={errors}
        startTime={values.startTime}
        comboboxProps={comboboxProps}
      />
      <AppointmentDateTimeFields
        values={values}
        onChange={onChange}
        errors={errors}
        comboboxProps={comboboxProps}
        popoverProps={popoverProps}
      />
      <AppointmentRecurrenceFields
        values={values}
        onChange={onChange}
        hideToggle={identityProps.editing}
        recurringSeries={recurringSeries}
        comboboxProps={comboboxProps}
        popoverProps={popoverProps}
      />
      <AppointmentLocationFields
        values={values}
        onChange={onChange}
        errors={errors}
        sites={sites}
        siteOptions={siteOptions}
        firstSiteId={firstSiteId}
        rooms={rooms}
        portableDevices={portableDevices}
        fixedInRoom={fixedInRoom}
        busyByRoom={busyByRoom}
        busyByDevice={busyByDevice}
        bookingStart={bookingStart}
        bookingEnd={bookingEnd}
        onViewRoomAvailability={onViewRoomAvailability}
        comboboxProps={comboboxProps}
      />
    </>
  );
}
